/**
 * Editorial motion layer.
 *
 * Adds scroll-reveal and load-in choreography on top of Horizon. Nothing here
 * changes behaviour or layout — it only toggles attributes that design-system.css
 * animates.
 *
 * Safety rules this file follows, because hiding content behind JS is how a
 * storefront ends up blank:
 *  - The hiding styles key off [data-ds-reveal], which only ever exists because
 *    this script added it. If the script never runs, everything renders normally.
 *  - Elements are only hidden once an IntersectionObserver is confirmed to exist.
 *  - A failsafe timer reveals everything regardless, so a missed intersection
 *    can never leave a section permanently invisible.
 *  - Anything already in view on first paint is revealed immediately.
 *  - Re-scans after Section Rendering API swaps (filtering, pagination, cart).
 */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (REDUCED.matches) return;
  if (!('IntersectionObserver' in window)) return;

  const ATTR = 'data-ds-reveal';
  const FAILSAFE_MS = 2600;

  /* Blocks worth revealing. Deliberately curated: revealing every div makes the
     page feel noisy and fights Horizon's own carousels and drawers. */
  const SELECTOR = [
    '.product-grid__item',
    '[class*="collection-card"]',
    '.resource-card',
    '.shopify-section .text-block',
    '.shopify-section .group-block > .group-block-content > .button',
    'footer .group-block > .group-block-content',
    '.ag-card',
    '.ag-hero__main',
    '.ag-hero__tile',
    '.ag-trust__item',
    '.ag-cat-pill',
    '.ag-cats__tile',
    '.ag-finder-card',
    '.ag-craftsmanship__card',
    '.ag-line-card',
    '.ag-review-card',
    '.ag-ugc__tile',
    '.ag-faq__item',
    '.ag-brand-story__media',
    '.ag-brand-story__copy',
    '.ag-fabric-card',
    '.ag-rail__head',
  ].join(',');

  /* Never animate things inside overlays, carousels or the hero — those have
     their own motion and would flicker or get stuck off-screen. */
  const EXCLUDE = [
    'dialog',
    '.theme-drawer__dialog',
    '.menu-drawer',
    '.cart-drawer',
    'slideshow-component',
    '.hero',
    '.quick-add-modal',
    '[data-ds-reveal]',
  ].join(',');

  let observer;

  const reveal = (el) => {
    el.setAttribute(ATTR, 'in');
    if (observer) observer.unobserve(el);
  };

  const revealAll = (root = document) => {
    root.querySelectorAll(`[${ATTR}="out"]`).forEach(reveal);
  };

  const makeObserver = () =>
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );

  const scan = (root = document) => {
    let candidates;
    try {
      candidates = root.querySelectorAll(SELECTOR);
    } catch (e) {
      return;
    }

    /* Stagger is per-row within a grid so a 4-up row cascades rather than
       every card in a long list waiting its turn. */
    const groupIndex = new Map();

    candidates.forEach((el) => {
      if (el.closest(EXCLUDE)) return;
      if (el.hasAttribute(ATTR)) return;

      const parent = el.parentElement;
      const i = groupIndex.get(parent) ?? 0;
      groupIndex.set(parent, i + 1);
      el.style.setProperty('--ds-reveal-delay', `${Math.min(i, 5) * 70}ms`);

      const rect = el.getBoundingClientRect();
      const alreadyVisible = rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

      el.setAttribute(ATTR, 'out');
      if (alreadyVisible) {
        /* Next frame, so the transition still plays on first paint. */
        requestAnimationFrame(() => requestAnimationFrame(() => reveal(el)));
      } else {
        observer.observe(el);
      }
    });
  };

  const start = () => {
    observer = makeObserver();
    scan();

    /* Hero choreography runs on load rather than on scroll. */
    document.querySelectorAll('.hero, .hero-wrapper, .ag-hero').forEach((h) => h.setAttribute('data-ds-hero', 'in'));

    /* Horizon swaps section markup for filters, pagination and cart updates. */
    const rescan = () => scan();
    document.addEventListener('shopify:section:load', (e) => scan(e.target || document));

    let pending = false;
    new MutationObserver((records) => {
      if (pending) return;
      const added = records.some((r) => r.addedNodes.length);
      if (!added) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        rescan();
      });
    }).observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => revealAll(), FAILSAFE_MS);
    window.addEventListener('pagehide', () => revealAll());
  };

  /* If motion preference flips to reduced mid-session, stop hiding anything. */
  REDUCED.addEventListener?.('change', (e) => {
    if (e.matches) {
      observer?.disconnect();
      revealAll();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
